/**
 * The session handshake the BigCommerce connect popup uses, and how its failures are worded for a
 * merchant.
 *
 * Kept out of the screen so the timeout and the retry classification can be tested without driving
 * the whole component.
 */

/** How long to wait for the embedded app to answer before giving up. */
const SESSION_REQUEST_TIMEOUT_MS = 10_000;

/**
 * Asks the embedded app for its session token, resolving with it and the store it belongs to.
 *
 * Simpler than the Shopify equivalent on purpose. Shopify ID tokens expire a minute after issue, so
 * that flow has to mint a fresh one at the moment the merchant clicks (ENG-6681). Ours is a token we
 * issue ourselves at `/load` with an hour's life, so a single request when the screen opens is
 * enough and the human pause before the click is not racing anything.
 */
export function requestBigCommerceSession(
  appOrigin: string,
): Promise<{ token: string; storeHash: string | undefined }> {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.clearTimeout(timer);
      window.removeEventListener("message", listener);
    };
    const listener = (event: MessageEvent) => {
      // The origin check is the security boundary: any page can post to this window, and what comes
      // back is a live credential for a merchant's store.
      if (event.origin !== appOrigin) return;
      const data = event.data as
        | {
            type?: string;
            token?: string;
            storeHash?: string;
            message?: string;
          }
        | undefined;
      if (!data) return;
      if (data.type === "fs.connect.bigCommerceSession" && data.token) {
        cleanup();
        resolve({ token: data.token, storeHash: data.storeHash });
      } else if (data.type === "fs.connect.bigCommerceSessionError") {
        cleanup();
        reject(
          new Error(data.message ?? "The app could not identify your store."),
        );
      }
    };
    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error("The BigCommerce app didn't respond."));
    }, SESSION_REQUEST_TIMEOUT_MS);
    window.addEventListener("message", listener);
    window.opener?.postMessage(
      { type: "fs.connect.requestBigCommerceSession" },
      appOrigin,
    );
  });
}

/**
 * Whether the embedded app may be handed a session token.
 *
 * Load-bearing: without it, luring a signed-in Dashboard user to
 * `/connect-bigcommerce?appOrigin=https://attacker.example` would hand an attacker a live session
 * token for a merchant's store. So it is an allowlist of exact origins, never a shape check — an
 * unanchored "any https host" pattern reads like a check and permits everything.
 *
 * Takes the Dashboard's own hostname rather than reading `window`, so both branches are testable.
 */
export function isAllowedBigCommerceAppOrigin(
  origin: string,
  dashboardHostname: string,
): boolean {
  if (origin === "https://bigcommerce.freightsimple.com") return true;
  if (origin === "https://bigcommerce.fspreprod.com") return true;
  if (origin === "https://bigcommerce.freightsimpledemo.com") return true;
  // Local development only: the app runs on another localhost port, or behind a cloudflared tunnel
  // (ENG-6784 picked cloudflared over ngrok — ngrok-free's interstitial breaks iframe fetches).
  // Production builds serve from dashboard.freightsimple.com, so this branch is dead there.
  if (dashboardHostname === "localhost" || dashboardHostname === "127.0.0.1") {
    return (
      /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(origin) ||
      /^https:\/\/[a-z0-9-]+\.trycloudflare\.com$/i.test(origin)
    );
  }
  return false;
}

/**
 * What the merchant is told, keyed on the backend's error code.
 *
 * The backend's own wording stays accurate for logs and support, but a store owner reading
 * "session token is invalid or expired" learns nothing they can act on.
 */
export function bigCommerceMerchantMessage(
  code: string,
  backendMessage: string,
): string {
  switch (code) {
    case "invalid_session_token":
      return "This window's connection to BigCommerce timed out. Close it and click Connect again.";
    case "link_request_failed":
      return "Couldn't reach FreightSimple. Check your connection and try again.";
    case "no_install_found":
      return "We have no installation on record for this store. Reinstall the app from BigCommerce and try again.";
    default:
      return backendMessage;
  }
}

/** Whether pressing Try again can clear this, rather than the merchant needing help. */
export function isBigCommerceRetryable(code: string): boolean {
  return (
    code === "invalid_session_token" ||
    code === "session_unavailable" ||
    code === "link_request_failed"
  );
}
